import style from './style.module.scss';

type PreviewSectionHeaderProps = {
    title: string;
    /** When provided, renders a count pill next to the title. */
    count?: number;
    /** When provided, the left chevron is rendered and clickable. */
    onPrev?: () => void;
    /** When provided, the right chevron is rendered and clickable. */
    onNext?: () => void;
    /** Visually disable the previous chevron (per Figma: opacity 30%). */
    prevDisabled?: boolean;
    /** Visually disable the next chevron (per Figma: opacity 30%). */
    nextDisabled?: boolean;
};

const CaretLeft = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M12.5 15 7.5 10l5-5"
            stroke="currentColor"
            strokeWidth="1.667"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const CaretRight = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M7.5 5l5 5-5 5"
            stroke="currentColor"
            strokeWidth="1.667"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Section header used in the simulated home page. Layout follows Figma
 * file HfdrKMPuxRduN2Tx38kLsR / node 3783-292996: a 14px bold title
 * paired with a black count pill on the left, and a pair of carets on
 * the right that the parent wires to a horizontal scroll handler.
 *
 * Carets dim to 30% opacity when their callback is omitted or when
 * `*Disabled` is true — matches the Figma "Pagination" component.
 */
export const PreviewSectionHeader = ({
    title,
    count,
    onPrev,
    onNext,
    prevDisabled,
    nextDisabled,
}: PreviewSectionHeaderProps) => {
    return (
        <div className={style.header}>
            <div className={style.title_row}>
                <h2 className={style.title}>{title}</h2>
                {count !== undefined && (
                    <span className={style.count_badge}>{count}</span>
                )}
            </div>
            {(onPrev || onNext) && (
                <div className={style.controls}>
                    <button
                        type="button"
                        className={style.caret}
                        onClick={onPrev}
                        disabled={prevDisabled || !onPrev}
                        aria-label="Previous"
                    >
                        <CaretLeft />
                    </button>
                    <button
                        type="button"
                        className={style.caret}
                        onClick={onNext}
                        disabled={nextDisabled || !onNext}
                        aria-label="Next"
                    >
                        <CaretRight />
                    </button>
                </div>
            )}
        </div>
    );
};
